import { useEffect, useState } from "react";
import {
  MDSCheckbox,
  MDSCheckboxGroup,
  MDSGridList,
  MDSRadio,
  MDSRadioGroup,
  MDSTextField,
} from "@/mds";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  StyledExplanation,
  StyledFormattingContentLeftAddon,
  StyledFormattingContentRightAddon,
  StyledHeading,
  StyledSubExplanation,
  StyledSubTitle,
} from "@/pages/integrations/shared/integrations.styles";
import { MdsHorizontalDivider } from "@/design-system/components/divider";
import { MdsButton, MdsButtonSize, MdsButtonVariant } from "@/design-system/components/button";
import { useAppStore } from "@/store";
import {
  EmailIntegrationFormattingMode,
  EmailIntegrationOrganizingMode,
  EmailIntegrationFormattingInstructions,
  EmailIntegrationOrganizingInstructions,
} from "@/store/email-senders/types";
import { IntegrationsFormattingCollections } from "@/pages/integrations/shared/integrations-formatting-collections";

export const IntegrationsFormattingForEmail = observer(() => {
  const { store } = useAppStore();
  const {
    formattingMode: formattingOption,
    formattingInstructions,
    organizingOption,
    organizingInstructions,
    setFormattingOption,
    setFormattingInstructions,
    setOrganizingOption,
    setOrganizingInstructions,
    organizingManualCollections,
    setOrganizingManualCollections,
  } = store.emailSenders;

  const [localFormattingInstructions, setLocalFormattingInstructions] =
    useState<EmailIntegrationFormattingInstructions>(formattingInstructions);
  const [localOrganizingInstructions, setLocalOrganizingInstructions] =
    useState<EmailIntegrationOrganizingInstructions>(organizingInstructions);

  useEffect(() => {
    setLocalFormattingInstructions(formattingInstructions);
    setLocalOrganizingInstructions(organizingInstructions);
  }, [formattingInstructions, organizingInstructions]);

  // Compute show states from local instructions
  const showFormattingCustomize = localFormattingInstructions !== null;
  const showOrganizingCustomize = localOrganizingInstructions !== null;

  const isFormattingSaveDisabled = localFormattingInstructions === formattingInstructions;
  const isOrganizingSaveDisabled = localOrganizingInstructions === organizingInstructions;

  const showSpecificCollections = organizingOption.includes("manual");

  // Generate formatting option items
  const formattingItems = [
    {
      id: "simple",
      textValue: "Simple",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSRadio value="simple" aria-label="Simple formatting" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <OptionTitle>Simple</OptionTitle>
          <OptionDescription>
            Preserve as much as the original email formatting and raw contents as possible
          </OptionDescription>
        </OptionLabel>
      ),
    },
    {
      id: "smart",
      textValue: "Smart",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSRadio value="smart" aria-label="Smart formatting" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <OptionTitle>Smart</OptionTitle>
          <OptionDescription>
            Allow Mem to pick the best format to capture key details for each email you save
          </OptionDescription>
          {showFormattingCustomize && (
            <ExtraContentContainer>
              <MdsHorizontalDivider />
              <ExtraContent>
                <StyledSubTitle>Customize how notes are formatted</StyledSubTitle>
                <StyledSubExplanation>
                  Provide more guidance to how to format notes from different kinds of emails
                </StyledSubExplanation>
                <StyledTextField
                  isTextArea
                  name="formattingInstructions"
                  aria-label="Customize how notes are formatted"
                  textAreaProps={{ rows: 3 }}
                  value={localFormattingInstructions}
                  onChange={setLocalFormattingInstructions}
                  placeholder="For newsletters, list key points and preserve all resource links. For flight confirmations, only include confirmation codes and travel details. For customer intros, create meeting briefing notes. For customer intros, create meeting briefing notes"
                />
                <LeftStyles>
                  <MdsButton
                    label="Cancel"
                    size={MdsButtonSize.Small}
                    variant={MdsButtonVariant.Outlined}
                    onClick={() => {
                      // revert to local value
                      setLocalFormattingInstructions(formattingInstructions);
                    }}
                  />
                  <MdsButton
                    label="Save Settings"
                    size={MdsButtonSize.Small}
                    variant={MdsButtonVariant.FilledDark}
                    onClick={() => setFormattingInstructions(localFormattingInstructions)}
                    isDisabled={isFormattingSaveDisabled}
                  />
                </LeftStyles>
              </ExtraContent>
            </ExtraContentContainer>
          )}
        </OptionLabel>
      ),
      rightAddon: !showFormattingCustomize && (
        <StyledFormattingContentRightAddon>
          <MdsButton
            label="Customize"
            size={MdsButtonSize.Small}
            variant={MdsButtonVariant.Outlined}
            onClick={() => setLocalFormattingInstructions("")}
          />
        </StyledFormattingContentRightAddon>
      ),
    },
  ];

  // Generate organizing option items
  const organizingItems = [
    {
      id: "specific-collections",
      label: "Specific collections",
      textValue: "Specific Collections",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSCheckbox value="manual" aria-label="Specific collections" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <OptionTitle>Specific Collections</OptionTitle>
          <OptionDescription>
            Always add notes created from email to one or more collections
          </OptionDescription>
          {showSpecificCollections && (
            <ExtraContentContainer>
              <ExtraContent>
                <IntegrationsFormattingCollections
                  organizingManualCollections={organizingManualCollections}
                  setOrganizingManualCollections={setOrganizingManualCollections}
                />
              </ExtraContent>
            </ExtraContentContainer>
          )}
        </OptionLabel>
      ),
    },
    {
      id: "smart-organizing",
      label: "Smart organizing",
      textValue: "Smart",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSCheckbox value="smart" aria-label="Smart organizing" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <OptionTitle>Smart</OptionTitle>
          <OptionDescription>
            Allow Mem to pick the collections to organize each email you save
          </OptionDescription>
          {showOrganizingCustomize && (
            <ExtraContentContainer>
              <MdsHorizontalDivider />
              <ExtraContent>
                <StyledSubTitle>Customize how emails are organized</StyledSubTitle>
                <StyledSubExplanation>
                  Provide more guidance to how to organize notes from different kinds of emails
                </StyledSubExplanation>
                <StyledTextField
                  isTextArea
                  textAreaProps={{ rows: 3 }}
                  aria-label="Customize how emails are organized"
                  value={localOrganizingInstructions}
                  onChange={setLocalOrganizingInstructions}
                  placeholder="For customer intros, include them in the generic customer intros collection as well as the relevant product collection"
                  name="organizingInstructions"
                />
                <LeftStyles>
                  <MdsButton
                    label="Cancel"
                    size={MdsButtonSize.Small}
                    variant={MdsButtonVariant.Outlined}
                    onClick={() => {
                      // revert to local value
                      setLocalOrganizingInstructions(organizingInstructions);
                    }}
                  />
                  <MdsButton
                    label="Save Settings"
                    size={MdsButtonSize.Small}
                    variant={MdsButtonVariant.FilledDark}
                    onClick={() => setOrganizingInstructions(localOrganizingInstructions)}
                    isDisabled={isOrganizingSaveDisabled}
                  />
                </LeftStyles>
              </ExtraContent>
            </ExtraContentContainer>
          )}
        </OptionLabel>
      ),
      rightAddon: !showOrganizingCustomize && (
        <StyledFormattingContentRightAddon>
          <MdsButton
            label="Customize"
            size={MdsButtonSize.Small}
            variant={MdsButtonVariant.Outlined}
            onClick={() => setLocalOrganizingInstructions("")}
          />
        </StyledFormattingContentRightAddon>
      ),
    },
  ];

  return (
    <Container>
      <Section>
        <StyledHeading>Formatting</StyledHeading>
        <StyledExplanation>
          Mem will automatically format your emails, adding an informative title and a concise
          summary of key points. You can further customize your note at any time.
        </StyledExplanation>
        <MDSRadioGroup
          aria-label="Formatting"
          value={formattingOption}
          onChange={value => {
            const typedValue = value as EmailIntegrationFormattingMode;
            setFormattingOption(typedValue);

            if (typedValue !== "smart") {
              // immediately hide the form
              setLocalFormattingInstructions(null);
            }
          }}
        >
          <MDSGridList items={formattingItems} disallowTypeAhead />
        </MDSRadioGroup>
      </Section>

      <Section>
        <StyledHeading>Organizing</StyledHeading>
        <StyledExplanation>
          Mem can automatically organize notes created from your emails
        </StyledExplanation>

        <MDSCheckboxGroup
          aria-label="Organizing"
          value={organizingOption}
          onChange={value => {
            const typedValue = value as EmailIntegrationOrganizingMode[];
            setOrganizingOption(typedValue);

            if (!typedValue.includes("smart")) {
              setLocalOrganizingInstructions(null);
            }
          }}
        >
          <MDSGridList items={organizingItems} disallowTypeAhead />
        </MDSCheckboxGroup>
      </Section>
    </Container>
  );
});

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  position: relative;
  overflow: visible;
  padding-top: ${({ theme }) => theme.spacing.sm};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const OptionLabel = styled.div`
  display: flex;
  flex-direction: column;

  gap: ${({ theme }) => theme.spacing.xs};
`;

const OptionTitle = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

const OptionDescription = styled.div`
  color: ${({ theme }) => theme.colors.grey.x500};
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
`;

const ExtraContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
`;

const ExtraContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
  position: relative;
  transform-origin: top;
  transition: transform 0.2s ease-in-out;
`;

export const StyledTextField = styled(MDSTextField)`
  margin-top: ${({ theme }) => theme.spacing.sm};

  textarea {
    font-size: ${({ theme }) => theme.fontSizes.small};
  }
`;

const LeftStyles = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;
